/**
 * Unified AI Assistant
 * 
 * Consolidates AIRegulationAssistant and EnterpriseChatbot into a single component.
 * 
 * Modes:
 * - Framework Mode: When a framework is selected, provides framework-specific guidance
 * - General GRC Mode: General compliance questions
 * - Policy Mode: Help with policy drafting
 * - Risk Mode: Risk assessment and analysis
 * - Audit Mode: Audit preparation support
 * 
 * Disclaimer: All advice is informational only and NOT legally binding.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Brain, Send, Loader2, AlertTriangle, X, Sparkles, Minimize2, Maximize2,
  Shield, FileText, CheckCircle, HelpCircle, MessageSquare, ChevronDown, LifeBuoy
} from 'lucide-react';
import { Framework } from '../../types/grc';
import { grcService } from '../../services/grcService';
import { chatbotService } from '../../services/enterpriseService';
import { useI18n } from '../../context/I18nContext';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  sources?: { type: string; title: string; relevance: number }[];
}

interface AssistantMode {
  id: string;
  name: string;
  nameSr: string;
  description: string;
  descriptionSr: string;
  icon: React.ComponentType<any>;
  color: string;
}

interface UnifiedAIAssistantProps {
  framework?: Framework | null;
  onClose?: () => void;
  isFloating?: boolean;
  defaultMode?: string;
}

const assistantModes: AssistantMode[] = [
  {
    id: 'framework',
    name: 'Framework Assistant',
    nameSr: 'Asistent za regulativu',
    description: 'Framework-specific guidance',
    descriptionSr: 'Pomoć specifična za regulativu',
    icon: Brain,
    color: 'indigo'
  },
  {
    id: 'general',
    name: 'General GRC',
    nameSr: 'Opći GRC',
    description: 'General compliance questions',
    descriptionSr: 'Opća pitanja o usklađenosti',
    icon: Shield,
    color: 'teal'
  },
  {
    id: 'policy',
    name: 'Policy Helper',
    nameSr: 'Pomoć za politike',
    description: 'Help with policy drafting',
    descriptionSr: 'Pomoć s izradom politika',
    icon: FileText,
    color: 'blue'
  },
  {
    id: 'risk',
    name: 'Risk Analysis',
    nameSr: 'Analiza rizika',
    description: 'Risk assessment & analysis',
    descriptionSr: 'Procjena i analiza rizika',
    icon: AlertTriangle,
    color: 'orange'
  },
  {
    id: 'audit',
    name: 'Audit Prep',
    nameSr: 'Priprema revizije',
    description: 'Help prepare for audits',
    descriptionSr: 'Pomoć s pripremom revizije',
    icon: CheckCircle,
    color: 'green'
  },
  {
    id: 'support',
    name: 'Tech Support',
    nameSr: 'Tehnička podrška',
    description: 'Report issues to technical support',
    descriptionSr: 'Prijavite problem tehničkoj podršci',
    icon: LifeBuoy,
    color: 'rose'
  }
];

const getQuickPrompts = (mode: string, framework: Framework | null, language: string): string[] => {
  const isSr = language === 'sr';
  
  if (mode === 'framework' && framework) {
    const isSerbian = /ZAKON|ZIB|ZOINS/i.test(framework.code);
    if (isSerbian || isSr) {
      return [
        `Koji su ključni zahtevi ${framework.name}?`,
        `Koje su kazne za nepoštovanje?`,
        `Kako da poboljšam skor usklađenosti?`,
        `Šta treba da uradim za kontrole koje nisu usklađene?`,
      ];
    }
    return [
      `What are the key requirements of ${framework.name}?`,
      `What penalties exist for non-compliance?`,
      `How can I improve my compliance score?`,
      `What should I do about non-compliant controls?`,
    ];
  }
  
  if (mode === 'policy') {
    return isSr ? [
      'Napravi mi nacrt politike o prihvatljivoj upotrebi',
      'Koje su ključne sekcije politike informacione bezbednosti?',
      'Pomozi mi da ažuriram politiku pristupa',
    ] : [
      'Draft an acceptable use policy for me',
      'What are key sections of an information security policy?',
      'Help me update our access control policy',
    ];
  }
  
  if (mode === 'risk') {
    return isSr ? [
      'Koji su glavni rizici za naš sistem?',
      'Kako proceniti rizik od curenja podataka?',
      'Predloži mere za smanjenje rizika',
    ] : [
      'What are the main risks for our system?',
      'How to assess data breach risk?',
      'Suggest risk mitigation measures',
    ];
  }
  
  if (mode === 'audit') {
    return isSr ? [
      'Kako se pripremiti za SOC 2 reviziju?',
      'Koji dokumenti su potrebni za ISO 27001 audit?',
      'Koje su česte greške tokom revizije?',
    ] : [
      'How to prepare for SOC 2 audit?',
      'What documents are needed for ISO 27001 audit?',
      'What are common mistakes during audits?',
    ];
  }
  
  if (mode === 'support') {
    return isSr ? [
      'Imam problem sa sistemom',
      'Nešto ne radi kako treba',
      'Potrebna mi je pomoć sa konfiguracijom',
    ] : [
      'I have a system issue',
      'Something is not working properly',
      'I need help with configuration',
    ];
  }
  
  // General mode
  return isSr ? [
    'Kako implementirati ISO 27001 kontrolu?',
    'Objasni razliku između SOC 1 i SOC 2',
    'Šta je GDPR usklađenost?',
  ] : [
    'How to implement an ISO 27001 control?',
    'Explain the difference between SOC 1 and SOC 2',
    'What is GDPR compliance?',
  ];
};

// Helper to render inline markdown
const renderInlineMarkdown = (text: string) => {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    return part;
  });
};

export const UnifiedAIAssistant: React.FC<UnifiedAIAssistantProps> = ({
  framework,
  onClose,
  isFloating = false,
  defaultMode = 'general',
}) => {
  const { language } = useI18n();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMinimized, setIsMinimized] = useState(isFloating);
  const [activeMode, setActiveMode] = useState(defaultMode);
  const [showModeSelector, setShowModeSelector] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [showSupportForm, setShowSupportForm] = useState(false);
  const [supportTicket, setSupportTicket] = useState({ subject: '', description: '', priority: 'medium' as 'low' | 'medium' | 'high' | 'critical', category: 'bug' });
  const [sendingTicket, setSendingTicket] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const isSr = language === 'sr';

  // If framework is provided and we're in general mode, switch to framework mode
  useEffect(() => {
    if (framework && activeMode !== 'framework') {
      setActiveMode('framework');
    }
  }, [framework]);

  // Initial greeting when mode or framework changes
  useEffect(() => {
    const greeting = getGreeting();
    setMessages([{
      id: 'greeting',
      role: 'assistant',
      content: greeting,
      timestamp: new Date()
    }]);
  }, [activeMode, framework?.id]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getGreeting = (): string => {
    const currentMode = assistantModes.find(m => m.id === activeMode) || assistantModes[1];
    
    if (activeMode === 'framework' && framework) {
      const isSerbian = /ZAKON|ZIB|ZOINS/i.test(framework.code);
      if (isSerbian || isSr) {
        return `Zdravo! Ja sam vaš AI asistent za regulaciju **${framework.name}**.\n\nMogu vam pomoći sa:\n- Objašnjenjima zahteva i članaka\n- Savetima za postizanje usklađenosti\n- Analizom trenutnog statusa\n\n⚠️ *Napomena: Ovo je informativno uputstvo i NIJE pravno obavezujuće.*`;
      }
      return `Hello! I'm your AI assistant for **${framework.name}**.\n\nI can help you with:\n- Explaining requirements and articles\n- Advice on achieving compliance\n- Analyzing your current status\n\n⚠️ *Note: This is informational guidance only and is NOT legally binding.*`;
    }
    
    const modeName = isSr ? currentMode.nameSr : currentMode.name;
    const modeDesc = isSr ? currentMode.descriptionSr : currentMode.description;
    
    if (activeMode === 'support') {
      if (isSr) {
        return `Zdravo! Ja sam vaš asistent za **tehničku podršku**.\n\nMožete mi opisati problem ili koristiti dugme 📋 **Prijavi problem** da pošaljete formalni zahtev tehničkoj podršci (Global Admin timu).\n\nKako vam mogu pomoći?`;
      }
      return `Hello! I'm your **Technical Support** assistant.\n\nYou can describe your issue here or use the 📋 **Report Issue** button to send a formal ticket to technical support (Global Admin team).\n\nHow can I help you?`;
    }

    if (isSr) {
      return `Zdravo! Ja sam vaš AI asistent za **${modeName}**.\n\n${modeDesc}.\n\n⚠️ *Napomena: Ovo je informativno uputstvo i NIJE pravno obavezujuće.*\n\nKako vam mogu pomoći?`;
    }
    return `Hello! I'm your AI assistant for **${modeName}**.\n\n${modeDesc}.\n\n⚠️ *Note: This is informational guidance only and is NOT legally binding.*\n\nHow can I help you?`;
  };

  const handleSubmitTicket = async () => {
    if (!supportTicket.subject.trim() || !supportTicket.description.trim()) return;
    setSendingTicket(true);
    try {
      await chatbotService.reportIssue(supportTicket);
      const confirmMsg: Message = {
        id: `system-${Date.now()}`,
        role: 'assistant',
        content: isSr 
          ? `✅ **Zahtev je uspješno poslan tehničkoj podršci!**\n\n**Predmet:** ${supportTicket.subject}\n**Prioritet:** ${supportTicket.priority}\n**Kategorija:** ${supportTicket.category}\n\nGlobal Admin tim će biti obaviješten i odgovoriti u najkraćem roku.`
          : `✅ **Support ticket submitted successfully!**\n\n**Subject:** ${supportTicket.subject}\n**Priority:** ${supportTicket.priority}\n**Category:** ${supportTicket.category}\n\nThe Global Admin team has been notified and will respond as soon as possible.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, confirmMsg]);
      setSupportTicket({ subject: '', description: '', priority: 'medium', category: 'bug' });
      setShowSupportForm(false);
    } catch {
      const errorMsg: Message = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: isSr ? '❌ Greška pri slanju zahteva. Pokušajte ponovo.' : '❌ Error sending support ticket. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setSendingTicket(false);
    }
  };

  const sendMessage = useCallback(async (text?: string) => {
    const question = text || input.trim();
    if (!question || isLoading) return;

    const userMsg: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: question,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      let result: { success: boolean; answer?: string; error?: string; sources?: any[] };
      
      if (activeMode === 'framework' && framework) {
        // Use framework-specific API
        result = await grcService.aiRegulationChat(framework.id, question);
      } else {
        // Use enterprise chatbot API
        const response = await chatbotService.sendMessage({
          message: question,
          mode: activeMode,
          conversationId: conversationId || undefined
        });
        result = {
          success: !!response,
          answer: response?.response || response?.message,
          sources: response?.sources
        };
        if (response?.conversation_id) {
          setConversationId(response.conversation_id);
        }
      }

      const assistantMsg: Message = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: result.success && result.answer 
          ? result.answer 
          : (result.error || generateFallbackResponse(question)),
        timestamp: new Date(),
        sources: result.sources
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (error) {
      console.error('AI Assistant error:', error);
      const errorMsg: Message = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: generateFallbackResponse(question),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  }, [input, framework, isLoading, activeMode, conversationId]);

  // Generate a helpful fallback response when API isn't available
  const generateFallbackResponse = (question: string): string => {
    const q = question.toLowerCase();
    
    if (q.includes('implement') || q.includes('implementir')) {
      return isSr 
        ? 'Za implementaciju kontrole, pratite ove korake:\n1. Identifikujte zahteve kontrole\n2. Mapirajte postojeće procese\n3. Identifikujte praznine\n4. Izradite plan implementacije\n5. Dokumentujte politike i procedure\n6. Obučite osoblje\n7. Testirajte i validirajte'
        : 'To implement a control, follow these steps:\n1. Identify the control requirements\n2. Map existing processes\n3. Identify gaps\n4. Create an implementation plan\n5. Document policies and procedures\n6. Train staff\n7. Test and validate';
    }
    
    if (q.includes('audit') || q.includes('revizij')) {
      return isSr
        ? 'Za pripremu revizije:\n1. Prikupite svu potrebnu dokumentaciju\n2. Proverite da li su sve kontrole implementirane\n3. Pripremite dokaze za svaku kontrolu\n4. Obučite osoblje o procesu revizije\n5. Izvršite internu prethodnu procenu'
        : 'To prepare for an audit:\n1. Gather all required documentation\n2. Verify all controls are implemented\n3. Prepare evidence for each control\n4. Train staff on the audit process\n5. Perform internal pre-assessment';
    }
    
    if (q.includes('polic') || q.includes('politika')) {
      return isSr
        ? 'Tipična struktura politike bezbednosti uključuje:\n1. Svrha i opseg\n2. Odgovornosti\n3. Definicije\n4. Pravila i zahtevi\n5. Procedure usklađenosti\n6. Izuzeći i odstupanja\n7. Verzioniranje i pregled'
        : 'A typical security policy structure includes:\n1. Purpose and scope\n2. Responsibilities\n3. Definitions\n4. Rules and requirements\n5. Compliance procedures\n6. Exceptions and deviations\n7. Version control and review';
    }
    
    if (q.includes('risk') || q.includes('rizik')) {
      return isSr
        ? 'Proces procene rizika:\n1. Identifikujte imovinu i resurse\n2. Identifikujte pretnje\n3. Identifikujte ranjivosti\n4. Procenite verovatnoću i uticaj\n5. Izračunajte nivo rizika\n6. Prioritetizujte rizike\n7. Definišite mere za smanjenje'
        : 'Risk assessment process:\n1. Identify assets and resources\n2. Identify threats\n3. Identify vulnerabilities\n4. Assess likelihood and impact\n5. Calculate risk level\n6. Prioritize risks\n7. Define mitigation measures';
    }
    
    return isSr
      ? 'Hvala na pitanju. Za detaljniji odgovor, proverite dokumentaciju regulative ili kontaktirajte vašeg GRC stručnjaka. Ovaj AI asistent pruža opšte smernice koje treba prilagoditi vašem specifičnom kontekstu.'
      : 'Thank you for your question. For a more detailed answer, please check the regulatory documentation or contact your GRC specialist. This AI assistant provides general guidance that should be adapted to your specific context.';
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([{
      id: 'greeting',
      role: 'assistant',
      content: getGreeting(),
      timestamp: new Date()
    }]);
    setConversationId(null);
  };

  const quickPrompts = getQuickPrompts(activeMode, framework || null, language);
  const currentMode = assistantModes.find(m => m.id === activeMode) || assistantModes[1];
  const ModeIcon = currentMode.icon;

  // Minimized floating button
  if (isFloating && isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 transition-all flex items-center justify-center group"
        title={isSr ? 'Otvori AI Asistenta' : 'Open AI Assistant'}
      >
        <Brain className="w-6 h-6" />
        {messages.length > 1 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {messages.filter(m => m.role === 'assistant').length}
          </span>
        )}
      </button>
    );
  }

  const containerClass = isFloating
    ? 'fixed bottom-6 right-6 z-50 w-[440px] h-[650px] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden'
    : 'bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 flex flex-col h-[600px] overflow-hidden';

  const colorClasses: Record<string, string> = {
    indigo: 'from-indigo-600 to-purple-600',
    teal: 'from-teal-600 to-cyan-600',
    blue: 'from-blue-600 to-indigo-600',
    orange: 'from-orange-500 to-red-500',
    green: 'from-green-600 to-teal-600',
    rose: 'from-rose-600 to-pink-600'
  };

  return (
    <div className={containerClass}>
      {/* Header */}
      <div className={`flex items-center gap-3 px-4 py-3 bg-gradient-to-r ${colorClasses[currentMode.color]} text-white flex-shrink-0`}>
        <ModeIcon className="w-5 h-5" />
        <div className="flex-1 min-w-0 relative">
          <button 
            onClick={() => setShowModeSelector(!showModeSelector)}
            className="flex items-center gap-2 hover:bg-white/10 px-2 py-1 rounded-lg transition-colors"
          >
            <div>
              <h3 className="text-sm font-semibold truncate">
                {isSr ? currentMode.nameSr : currentMode.name}
              </h3>
              <p className="text-xs text-white/70 truncate">
                {activeMode === 'framework' && framework ? framework.name : (isSr ? currentMode.descriptionSr : currentMode.description)}
              </p>
            </div>
            <ChevronDown className={`w-4 h-4 transition-transform ${showModeSelector ? 'rotate-180' : ''}`} />
          </button>
          
          {/* Mode Selector Dropdown */}
          {showModeSelector && (
            <div className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-10">
              {assistantModes.filter(m => m.id !== 'framework' || framework).map(mode => {
                const Icon = mode.icon;
                return (
                  <button
                    key={mode.id}
                    onClick={() => {
                      setActiveMode(mode.id);
                      setShowModeSelector(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      activeMode === mode.id ? 'bg-gray-100 dark:bg-gray-700' : ''
                    }`}
                  >
                    <Icon className={`w-4 h-4 text-${mode.color}-500`} />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {isSr ? mode.nameSr : mode.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {isSr ? mode.descriptionSr : mode.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          {activeMode === 'support' && (
            <button 
              onClick={() => setShowSupportForm(!showSupportForm)}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              title={isSr ? 'Prijavi problem' : 'Report Issue'}
            >
              <LifeBuoy className="w-4 h-4" />
            </button>
          )}
          <button 
            onClick={clearChat}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            title={isSr ? 'Obriši chat' : 'Clear chat'}
          >
            <MessageSquare className="w-4 h-4" />
          </button>
          {isFloating && (
            <button onClick={() => setIsMinimized(true)} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors">
              <Minimize2 className="w-4 h-4" />
            </button>
          )}
          {onClose && (
            <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Disclaimer */}
      <div className="px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 flex-shrink-0">
        <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          <span>{isSr ? 'Samo informativno — NIJE pravno obavezujuće' : 'Informational guidance only — NOT legally binding'}</span>
        </div>
      </div>

      {/* Support Ticket Form */}
      {showSupportForm && activeMode === 'support' && (
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0 space-y-3">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <LifeBuoy className="w-4 h-4 text-rose-500" />
            {isSr ? 'Prijavi problem tehničkoj podršci' : 'Report Issue to Tech Support'}
          </h4>
          <input
            type="text"
            value={supportTicket.subject}
            onChange={(e) => setSupportTicket(prev => ({ ...prev, subject: e.target.value }))}
            placeholder={isSr ? 'Predmet problema...' : 'Issue subject...'}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          <textarea
            value={supportTicket.description}
            onChange={(e) => setSupportTicket(prev => ({ ...prev, description: e.target.value }))}
            placeholder={isSr ? 'Opišite problem detaljno...' : 'Describe the issue in detail...'}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
            rows={3}
          />
          <div className="flex gap-2">
            <select
              value={supportTicket.priority}
              onChange={(e) => setSupportTicket(prev => ({ ...prev, priority: e.target.value as any }))}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="low">{isSr ? 'Nizak prioritet' : 'Low Priority'}</option>
              <option value="medium">{isSr ? 'Srednji prioritet' : 'Medium Priority'}</option>
              <option value="high">{isSr ? 'Visok prioritet' : 'High Priority'}</option>
              <option value="critical">{isSr ? 'Kritičan' : 'Critical'}</option>
            </select>
            <select
              value={supportTicket.category}
              onChange={(e) => setSupportTicket(prev => ({ ...prev, category: e.target.value }))}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="bug">{isSr ? 'Greška' : 'Bug'}</option>
              <option value="feature">{isSr ? 'Zahtev za funkcionalnost' : 'Feature Request'}</option>
              <option value="config">{isSr ? 'Konfiguracija' : 'Configuration'}</option>
              <option value="access">{isSr ? 'Problem sa pristupom' : 'Access Issue'}</option>
              <option value="other">{isSr ? 'Ostalo' : 'Other'}</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowSupportForm(false)}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              {isSr ? 'Otkaži' : 'Cancel'}
            </button>
            <button
              onClick={handleSubmitTicket}
              disabled={!supportTicket.subject.trim() || !supportTicket.description.trim() || sendingTicket}
              className="flex-1 px-3 py-2 text-sm bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {sendingTicket ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {isSr ? 'Pošalji' : 'Submit Ticket'}
            </button>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {messages.map(msg => (
          <div
            key={msg.id}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${
              msg.role === 'user'
                ? 'bg-indigo-600 text-white rounded-br-md'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-bl-md'
            }`}>
              {msg.role === 'assistant' ? (
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  {msg.content.split('\n').map((line, i) => {
                    if (line.startsWith('**') && line.endsWith('**')) {
                      return <p key={i} className="font-semibold mb-1">{line.slice(2, -2)}</p>;
                    }
                    if (line.startsWith('- ')) {
                      return <li key={i} className="ml-4 list-disc">{renderInlineMarkdown(line.slice(2))}</li>;
                    }
                    if (/^\d+\./.test(line)) {
                      return <li key={i} className="ml-4 list-decimal">{renderInlineMarkdown(line.replace(/^\d+\.\s*/, ''))}</li>;
                    }
                    if (line.startsWith('⚠️')) {
                      return <p key={i} className="text-amber-600 dark:text-amber-400 text-xs italic mt-2">{renderInlineMarkdown(line)}</p>;
                    }
                    if (line.trim() === '') return <br key={i} />;
                    return <p key={i} className="mb-1">{renderInlineMarkdown(line)}</p>;
                  })}
                </div>
              ) : (
                <span>{msg.content}</span>
              )}
              <span className={`block text-[10px] mt-1 ${
                msg.role === 'user' ? 'text-indigo-200' : 'text-gray-400 dark:text-gray-500'
              }`}>
                {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">{isSr ? 'Razmišljam...' : 'Thinking...'}</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick Prompts */}
      {messages.length <= 2 && quickPrompts.length > 0 && (
        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            {isSr ? 'Predložena pitanja:' : 'Suggested questions:'}
          </p>
          <div className="flex flex-wrap gap-2">
            {quickPrompts.slice(0, 3).map((prompt, i) => (
              <button
                key={i}
                onClick={() => sendMessage(prompt)}
                className="text-xs px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors truncate max-w-[200px]"
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isSr ? 'Unesite pitanje...' : 'Type your question...'}
            className="flex-1 resize-none border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-2.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent max-h-24"
            rows={1}
            disabled={isLoading}
          />
          <button
            onClick={() => sendMessage()}
            disabled={!input.trim() || isLoading}
            className="p-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnifiedAIAssistant;
